import { useState } from 'react';
import { subDays } from 'date-fns';
import { Clock, DollarSign, Loader, PhoneCall, UserCheck } from 'lucide-react';
import { DateRange } from 'react-day-picker';
import {
  Bar,
  BarChart,
  Cell,
  Pie,
  PieChart,
  PolarAngleAxis,
  PolarGrid,
  PolarRadiusAxis,
  Radar,
  RadarChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { DateRangePicker } from '@/components/ui/date-range-picker';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import useDashboard from '@/hooks/useDashboard';
import { AgentPerformance, DashboardMetrics, KpiDataPoint } from '@/types';
import { MetricCard } from './MetricCard';

const COLORS = {
  greeting: '#8ecae6',
  closing: '#219ebc',
  rapport: '#023047',
  objections: '#ffb703',
  problemIdentification: '#fb8500',
  salesObjections: '#606c38',
  qualification: '#283618',
  callerObjective: '#34495e',
};

const transformMetricsToKpiData = (metrics: DashboardMetrics): KpiDataPoint[] => {
  const calculateAverage = (values: number[]): number => {
    if (values.length === 0) return 0;
    const sum = values.reduce((acc, val) => acc + val, 0);
    return parseFloat((sum / values.length).toFixed(2));
  };

  return [
    // 1. Agent name introduction
    {
      name: 'Agent name introduction',
      value: metrics.greeting_scores.agent_name_introduction,
      category: 'greeting',
    },
    // 2. Company name used
    {
      name: 'Company name used',
      value: metrics.greeting_scores.company_name_used,
      category: 'greeting',
    },
    // 3. Recap conversation
    {
      name: 'Recap conversation',
      value: metrics.closing_scores.recap_conversation,
      category: 'closing',
    },
    // 4. Confirm no other needs
    {
      name: 'Confirm no other needs',
      value: metrics.closing_scores.confirm_no_other_needs,
      category: 'closing',
    },
    // 5. Thank for business
    {
      name: 'Thank for business',
      value: metrics.closing_scores.thank_for_business,
      category: 'closing',
    },
    // 6. Build rapport
    {
      name: 'Build rapport',
      value: calculateAverage([
        metrics.rapport_scores.positivity,
        metrics.rapport_scores.clarity_of_speech,
        metrics.rapport_scores.confident,
        metrics.rapport_scores.agent_paused,
      ]),
      category: 'rapport',
    },
    // 7. Overcome objections
    {
      name: 'Overcome objections',
      value: calculateAverage([
        metrics.objection_scores.immediate_service_unavailability,
        metrics.objection_scores.service_fee_concerns,
        metrics.objection_scores.scheduling_conflicts,
      ]),
      category: 'objections',
    },
    // 8. Problem identification
    {
      name: 'Problem identification',
      value: calculateAverage([
        metrics.problem_identification_scores.pest_identified,
        metrics.problem_identification_scores.location_of_pest,
        metrics.problem_identification_scores.duration_of_issue,
        metrics.problem_identification_scores.solicit_other_problems,
      ]),
      category: 'problemIdentification',
    },
    // 9. Overcome Sales Objections
    {
      name: 'Overcome Sales Objections',
      value: calculateAverage([
        metrics.sales_objection_scores.needs_time_to_decide,
        metrics.sales_objection_scores.pricing_requests,
        metrics.sales_objection_scores.in_person_estimates,
        metrics.sales_objection_scores.service_affordability,
        metrics.sales_objection_scores.landlord_approval,
      ]),
      category: 'salesObjections',
    },
    // 10. Qualify leads
    {
      name: 'Qualify leads',
      value: calculateAverage([
        metrics.qualification_scores.service_offered,
        metrics.qualification_scores.in_service_area,
      ]),
      category: 'qualification',
    },
    // 11. Agent upsell
    {
      name: 'Agent upsell',
      value: metrics.caller_objective_scores.agent_upsell,
      category: 'callerObjective',
    },
  ];
};

const formatPercentage = (value: number | null | undefined) => {
  return value != null ? `${value.toFixed(1)}%` : 'N/A';
};

const formatDuration = (seconds: number | null | undefined) => {
  if (!seconds) return '0:00.00';
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = (seconds % 60).toFixed(2);
  return `${minutes}:${remainingSeconds.padStart(5, '0')}`;
};

const CustomTooltip = ({ active, payload, label }: any) => {
  if (active && payload && payload.length) {
    const value = payload[0].value;
    return (
      <div className="p-3 bg-white border border-gray-200 rounded-lg shadow-sm">
        <p className="font-medium">{label}</p>
        <p className="text-sm text-gray-600">Score: {formatPercentage(value)}</p>
      </div>
    );
  }
  return null;
};

const groupKpisByCategory = (kpiData: KpiDataPoint[]) => {
  return kpiData.reduce(
    (acc, kpi) => {
      if (!kpi.category) return acc;
      if (!acc[kpi.category]) {
        acc[kpi.category] = [];
      }
      acc[kpi.category].push(kpi);
      return acc;
    },
    {} as Record<string, KpiDataPoint[]>,
  );
};

export function KpiDashboard() {
  // New state for refresh interval in minutes
  const [refreshInterval, setRefreshInterval] = useState<number>(1); // Default to 1 minute

  const [dateRange, setDateRange] = useState<DateRange | undefined>({
    from: subDays(new Date(), 7),
    to: new Date(),
  });
  const dashboardData = useDashboard(
    'scenario',
    dateRange?.from ? dateRange.from.toISOString().split('T')[0] : '',
    dateRange?.to ? dateRange.to.toISOString().split('T')[0] : '',
    refreshInterval * 60 * 1000, // Convert minutes to milliseconds
    Intl.DateTimeFormat().resolvedOptions().timeZone,
  );

  const handleDateRangeChange = (range: DateRange | undefined) => {
    console.log('Selected date range:', range);
    setDateRange(range);
  };

  if (dashboardData.isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader className="w-8 h-8" />
      </div>
    );
  }

  if (!dashboardData?.data) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <p className="text-gray-600">No data available</p>
      </div>
    );
  }
  const metrics = dashboardData.data;
  const kpiPerformance = transformMetricsToKpiData(dashboardData.data);
  const groupedKpis = groupKpisByCategory(kpiPerformance);

  return (
    <div className="flex min-h-screen bg-gray-50">
      <div className="flex flex-col flex-1 px-4 py-6 mx-auto max-w-7xl">
        {/* Header Section */}
        <div className="flex flex-col gap-4 mb-6 lg:flex-row lg:items-center lg:justify-between">
          <h1 className="text-2xl font-semibold tracking-tight text-center lg:text-3xl lg:text-left">Dashboard</h1>

          <div className="flex flex-col items-center gap-4 lg:flex-row lg:gap-6">
            {/* Date Range Picker */}
            <DateRangePicker dateRange={dateRange} onDateRangeChange={handleDateRangeChange} />

            {/* Refresh Interval Control */}
            <div className="flex items-center gap-2">
              <label htmlFor="refresh-interval" className="text-sm">
                Refresh Interval (min):
              </label>
              <input
                type="number"
                id="refresh-interval"
                min={1}
                value={refreshInterval}
                onChange={(e) => {
                  const value = Number(e.target.value);
                  setRefreshInterval(value >= 1 ? value : 1);
                }}
                className="w-16 px-2 py-1 border rounded"
              />
            </div>
          </div>
        </div>

        {/* Metric Cards */}
        <div className="grid grid-cols-2 gap-4 mb-8 lg:grid-cols-4">
          <MetricCard title="Total Calls" value={metrics.total_calls} icon={PhoneCall} trend={null} />
          <MetricCard title="Total Sales Calls" value={metrics.total_sales} icon={DollarSign} trend={null} />
          <MetricCard title="Avg Call Duration" value={formatDuration(metrics.duration)} icon={Clock} trend={null} />
          <MetricCard
            title="Overall Grade"
            value={`${metrics.overall_grade.toFixed(1)}%`}
            icon={UserCheck}
            trend={null}
          />
        </div>

        {/* KPI Performance Overview */}
        <div className="grid gap-6 mb-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-xl font-semibold">KPI Performance by Indicator/Category</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-[600px]">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={kpiPerformance.filter((kpi) => kpi.value !== null && kpi.value !== undefined)}
                    layout="vertical"
                    margin={{ left: 200, right: 30, top: 10, bottom: 10 }}
                  >
                    <XAxis type="number" domain={[0, 100]} tickFormatter={formatPercentage} tick={{ fontSize: 12 }} />
                    <YAxis
                      dataKey="name"
                      type="category"
                      width={200}
                      tick={{
                        fontSize: 12,
                        width: 180,
                        transform: 'translate(-5,0)',
                      }}
                    />
                    <Tooltip
                      formatter={(value: number) => formatPercentage(value)}
                      contentStyle={{
                        backgroundColor: 'white',
                        border: '1px solid #ccc',
                        borderRadius: '4px',
                      }}
                    />
                    <Bar dataKey="value" fill="#000" radius={[0, 4, 4, 0]} minPointSize={2}>
                      {kpiPerformance.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[entry.category as keyof typeof COLORS] || '#000'} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>

          {/* Category Performance */}
          <div className="grid grid-cols-1 gap-6 mb-6 lg:grid-cols-2">
            {/* Pie Chart */}
            <Card>
              <CardHeader>
                <CardTitle className="text-xl font-semibold">Category Distribution</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-[400px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={Object.entries(groupedKpis).map(([category, kpis]) => ({
                          name: category,
                          value: kpis.reduce((acc, kpi) => acc + (kpi.value || 0), 0) / (kpis.length || 1),
                        }))}
                        cx="50%"
                        cy="50%"
                        labelLine={true}
                        outerRadius={150}
                        fill="#8884d8"
                        dataKey="value"
                        label={({ value }) => `${value.toFixed(1)}%`}
                      >
                        {Object.keys(groupedKpis).map((category, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[category as keyof typeof COLORS] || '#000'} />
                        ))}
                      </Pie>
                      <Tooltip
                        content={({ active, payload }) => {
                          if (active && payload && payload.length) {
                            const data = payload[0];
                            const value = typeof data.value === 'number' ? data.value : 0;
                            return (
                              <div className="p-3 bg-white border border-gray-200 rounded-lg shadow-sm">
                                <p className="font-medium">{data.name}</p>
                                <p className="text-sm text-gray-600">{value.toFixed(1)}%</p>
                              </div>
                            );
                          }
                          return null;
                        }}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            {/* Radar Chart */}
            <Card>
              <CardHeader>
                <CardTitle className="text-xl font-semibold">Category Radar Analysis</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-[400px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <RadarChart
                      cx="50%"
                      cy="50%"
                      outerRadius="80%"
                      data={Object.entries(groupedKpis).map(([category, kpis]) => ({
                        category,
                        value: kpis.reduce((acc, kpi) => acc + (kpi.value || 0), 0) / (kpis.length || 1),
                      }))}
                    >
                      <PolarGrid gridType="polygon" />
                      <PolarAngleAxis
                        dataKey="category"
                        tick={{
                          fontSize: 12,
                          fill: '#666',
                          transform: 'translate(0,-5)',
                        }}
                      />
                      <PolarRadiusAxis angle={30} domain={[0, 100]} tickFormatter={(value) => `${value}%`} />
                      <Radar name="Performance" dataKey="value" stroke="#8884d8" fill="#8884d8" fillOpacity={0.3} />
                      <Tooltip content={<CustomTooltip />} />
                    </RadarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Agent Performance Section */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-2xl font-semibold">Agent Performance</h2>
          </div>

          <Card>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                {!metrics.agent_performance || metrics.agent_performance.length === 0 ? (
                  <div className="p-4 text-center text-gray-500">No agent performance data available</div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Agent</TableHead>
                        <TableHead>Calls</TableHead>
                        <TableHead>Avg Duration</TableHead>
                        <TableHead>Sales</TableHead>
                        <TableHead>Service</TableHead>
                        <TableHead>Grade</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {metrics.total_calls === 0 ? (
                        <TableRow>
                          <TableCell colSpan={6} className="text-center text-gray-500">
                            No agents found
                          </TableCell>
                        </TableRow>
                      ) : (
                        <>
                          {metrics.agent_performance.map((agent: AgentPerformance) => (
                            <TableRow key={agent.id}>
                              <TableCell className="font-medium">{agent.name}</TableCell>
                              <TableCell>{agent.call_count || 0}</TableCell>
                              <TableCell>{formatDuration(Number(agent.avg_duration) || 0)}</TableCell>
                              <TableCell>{agent.sales_count || 0}</TableCell>
                              <TableCell>{agent.service_count || 0}</TableCell>
                              <TableCell>
                                {typeof agent.call_grade === 'number'
                                  ? agent.call_grade.toFixed(1)
                                  : Number(agent.call_grade || 0).toFixed(1)}
                                %
                              </TableCell>
                            </TableRow>
                          ))}
                        </>
                      )}
                    </TableBody>
                  </Table>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
